import { html, TemplateResult } from "lit-html";
import { I18nService } from "Services/I18nService";
import { getDocLocale } from "Helper/metaHelper";
import { getCurrentMQ } from "Helper/domHelper";

function formatTime(time: string): string {
  if (!time) return "";

  const locale = getDocLocale();
  const timeIn = new Date(time);
  return timeIn.toLocaleTimeString(locale, { hour: "2-digit", minute: "2-digit" });
}

function formatWeekdayDate(time: string): string {
  if (!time) return "";

  const locale = getDocLocale();
  const timeIn = new Date(time);
  return timeIn.toLocaleDateString(locale, {
    weekday: "long",
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  });
}

export const mapLinkTemplate = (link: string, station: string): TemplateResult => {
  let label = "";
  if (station === "regio") {
    label = I18nService.getInstance().getKey("fraport.travel.components.trainDepartures.mapLinkRegio");
  } else if (station === "fern") {
    label = I18nService.getInstance().getKey("fraport.travel.components.trainDepartures.mapLinkFern");
  }
  return html`
    <a class="fra-e-link fra-e-link--with-icon" href="${link}" target="_blank">
      <div class="fra-e-link__label">${label}</div>
      <fra-e-icon class="fra-e-icon" icon-id="fra-icon-pin"></fra-e-icon>
    </a>
  `;
};

export const noDeparturesAvailable = (): TemplateResult => {
  return html`
    <div class="fra-m-train-departures--no-departures-message">
      <fra-e-icon class="fra-e-icon fra-e-button__icon" icon-id="fra-icon-notification"></fra-e-icon>
      <div>${I18nService.getInstance().getKey("fraport.travel.components.trainDepartures.noDeparturesMessage")}</div>
    </div>
  `;
};

export const dbLink = (dateTime: string, station: string): TemplateResult => {
  const url = createDBLink(dateTime, station);

  return html`
    <a class="fra-e-link fra-e-link--with-icon fra-e-link--animated-right" href=${url} target="_blank">
      ${I18nService.getInstance().getKey("fraport.travel.components.trainDepartures.dbLink")}
      <fra-e-icon class="fra-e-icon" icon-id="fra-icon-arrow-right-extern"></fra-e-icon>
    </a>
  `;
};

export const departuresListTemplate = (
  departures: Record<string, any>[],
  showDeparturesUpTo: number,
  state: Record<string, any>,
): TemplateResult => {
  let visibleDepartures = departures;

  if (departures.length > showDeparturesUpTo) {
    visibleDepartures = departures.slice(0, showDeparturesUpTo);
  }

  return html`
    ${visibleDepartures.map((departure, index) => {
      return renderListEntry(departure, state, index);
    })}
  `;
};

export const loadMoreDeparturesButtonTemplate = (setState: Function): TemplateResult => {
  return html`
    <button station="button" class="fra-e-button" @click=${() => setState({ showDeparturesUpTo: 8 })}>
      <span class="fra-e-button__label">
        ${I18nService.getInstance().getKey("fraport.travel.components.trainDepartures.loadMore")}
      </span>
    </button>
  `;
};

function renderListEntry(departure: Record<string, any>, state: Record<string, any>, index: number): TemplateResult {
  return html`
    ${isNextDay(departure.time, state, index)
      ? html`
          <dl class="fra-m-train-departures__list-entry">
            <div class="fra-e-list__entry fra-e-list__entry--weekday">${formatWeekdayDate(departure.time)}</div>
          </dl>
        `
      : ""}
    <dl class="fra-m-train-departures__list-entry">
      <div class="fra-e-list__entry">
        <dt class="fra-e-list__label fra-e-list__label--departure-time">${renderDepartureTime(departure)}</dt>
        <dt class="fra-e-list__label fra-e-aggregate fra-h-hide-mq2">
          <div class="fra-e-list__label--mobile-destination">${departure.transport.destination.name}</div>
          <div class="fra-e-list__label--mobile-train-info">
            <div class="fra-e-list__label--category">${renderTrainIdentifier(departure.transport)}</div>
            <div>${renderPlatform(departure)}</div>
          </div>
          ${departure.transport.via.length > 0 ? getIntermediateStations(departure.transport.via) : ""}
          ${renderInfoMessage(departure)} ${renderDBShopLink(departure.time, state.station)}
        </dt>
        <dt class="fra-e-list__label fra-h-show-mq2 fra-e-list__label--category">
          ${getCurrentMQ() !== "MQ1" ? renderTrainIdentifier(departure.transport) : ""}
        </dt>
        <dt class="fra-e-list__label fra-h-show-mq2">${departure.transport.destination.name}</dt>
        <dt class="fra-e-list__label fra-e-list__label--intermediate-stations fra-h-show-mq2">
          ${getIntermediateStations(departure.transport.via)}
        </dt>
        <dt class="fra-e-list__label fra-e-list__label--platform fra-h-show-mq2">${renderPlatform(departure)}</dt>
        <dd class="fra-e-list__desc fra-e-list__desc-link fra-h-show-mq2">
          ${renderDBShopLink(departure.time, state.station)}
        </dd>
      </div>
      ${renderInfoMessage(departure)}
    </dl>
  `;
}

function isNextDay(time: string, state: Record<string, any>, index: number): boolean {
  if (index > 0) {
    const previousDate = new Date(state.departures[index - 1].time).getDay();
    const departureDate = new Date(time).getDay();
    return previousDate < departureDate;
  }
  return false;
}

function getIntermediateStations(stations: Record<string, any>): TemplateResult {
  const filteredStations =
    stations.filter((station: { canceled: boolean }) => !station.canceled).length > 0
      ? stations
          .map((station: { name: string }) => station.name)
          .slice(0, 4)
          .join(", ")
      : "-";
  return html`
    <div class="fra-e-list__label--intermediate-stations">${filteredStations}</div>
  `;
}

function renderDBShopLink(dateTime: string, station: string): TemplateResult {
  const url = createDBLink(dateTime, station);

  return html`
    <a class="fra-e-link fra-e-link--with-icon fra-e-link--animated-right" href="${url}" target="_blank">
      <div class="fra-m-train-departures__connection-db-link">
        <fra-e-icon class="fra-e-icon" icon-id="fra-icon-arrow-right-extern"></fra-e-icon>
        <div class="fra-m-train-departures__connection-db-link--db-text">
          ${I18nService.getInstance().getKey("fraport.travel.components.trainDepartures.detailsTickets")}
          <span>${I18nService.getInstance().getKey("fraport.travel.components.trainDepartures.dbWebsite")}</span>
        </div>
      </div>
    </a>
  `;
}

function createDBLink(dateTime: string, station: string): string {
  const time = encodeURIComponent(formatTime(dateTime));
  const flightDate = new Date(dateTime);
  const date = `${flightDate.getFullYear()}-${flightDate.toLocaleDateString("en-US", {
    month: "2-digit",
  })}-${flightDate.toLocaleDateString("en-US", {
    day: "2-digit",
  })}`;
  const stationId = station === "fern" ? "Frankfurt(M) Flughafen Fernbf" : "Frankfurt(M) Flughafen Regionalbf";
  let langUrl = "www.bahn.de";
  if (getDocLocale() !== "de-DE") {
    langUrl = "int.bahn.de/en";
  }
  return `https://${langUrl}/buchung/start#sts=false&so=${stationId}&hd=${date}T${time}&hza=D`;
}

function renderDepartureTime(departure: Record<string, any>): TemplateResult {
  const time = formatTime(departure.time);
  const timeSchedule = formatTime(departure.timeSchedule);

  if (time !== timeSchedule) {
    return html`
      <div class="fra-m-train-departures__delayed">
        <div class="fra-m-train-departures__delayed--scheduled">${timeSchedule}</div>
        <div class="fra-m-train-departures__delayed--actual">${time}</div>
      </div>
    `;
  }
  return html`
    ${formatTime(departure.time)}
  `;
}

function renderInfoMessage(departure: Record<string, any>): TemplateResult {
  const message = departure.messages.find((message: Record<string, any>) => message.station === "CUSTOMER_REASON");
  if (message) {
    return html`
      <div class="fra-m-train-departures__list-entry--info-message">
        <fra-e-icon class="fra-e-icon fra-e-button__icon" icon-id="fra-icon-notification"></fra-e-icon>
        ${message.text}
      </div>
    `;
  }
  return html``;
}

function renderPlatform(departure: Record<string, any>): TemplateResult {
  const platform = departure.platform;
  const platformSchedule = departure.platformSchedule;

  if (platform !== platformSchedule) {
    return html`
      <div class="fra-m-train-departures__changed-platform">
        <div class="fra-m-train-departures__changed-platform--scheduled">${platformSchedule}</div>
        <div class="fra-m-train-departures__changed-platform--actual">${platform}</div>
      </div>
    `;
  }
  return html`
    ${platform}
  `;
}

function renderTrainIdentifier(train: Record<string, any>): TemplateResult {
  const compatibleIcons = ["ICE", "IC", "RB", "RE", "S"];
  const iconId = compatibleIcons.includes(train.category)
    ? `fra-icon-transportation-${train.category}`
    : "fra-icon-transportation-RB";
  const trainId = train.line !== undefined ? train.line : train.number;

  return html`
    <fra-e-icon class="fra-e-icon fra-m-train-departures__train-icon" icon-id="${iconId}"></fra-e-icon>
    <div>
      <b>${train.category}</b>
      ${trainId}
    </div>
  `;
}
